import { useEffect, useRef, useState } from "react";
import { PublicFooterLegalLinks } from "./public-legal.js";

export type PublicArticle = {
  slug: string;
  label: string;
  category: string;
  title: string;
  summary: string;
  publishedAt: string;
  updatedAt: string;
  readingMinutes: number;
  cover: string;
  coverAlt: string;
  accent: "blue" | "yellow";
  sections: Array<{
    id: string;
    title: string;
    paragraphs: string[];
    note?: { label: string; title: string; body: string };
    quote?: string;
  }>;
  closing: string;
  sources: Array<{ label: string; detail: string }>;
};

export const PUBLIC_ARTICLES: PublicArticle[] = [
  {
    slug: "welcome-to-yunchen-lab",
    label: "LAB NOTE / 01",
    category: "站点手记",
    title: "欢迎来到云宸LAB：这里如何记录市场、证据与复盘",
    summary: "这是一间持续生长的个人研究工作室。它不负责制造确定感，只负责把问题、资料和判断放回可以核对的位置。",
    publishedAt: "2026-09-04",
    updatedAt: "2026-09-04",
    readingMinutes: 6,
    cover: "/assets/article-welcome-cover-v1.png",
    coverAlt: "打开的研究笔记、资料纸页、放大镜和空椅组成的编辑插画",
    accent: "blue",
    sections: [
      {
        id: "why",
        title: "为什么要做这样一个网站",
        paragraphs: [
          "市场每天都在制造新消息，也在不断改写旧叙事。真正困难的往往不是找到更多观点，而是记住：当时看见了什么、依据是什么、还有哪些地方并不知道。",
          "云宸LAB从这个朴素的问题出发。这里会保存市场结构观察、行业线索、公开资料索引和研究方法，也会保留后来出现的反例。文章不是为了给一次判断盖章，而是为了让判断能够被重新打开。"
        ],
        quote: "把一时的观点写成可以回看的记录，比把它说得更确定更重要。"
      },
      {
        id: "how",
        title: "这里怎样组织一次研究",
        paragraphs: [
          "一篇文章通常从一个范围明确的问题开始。随后记录资料来源、时间和适用口径，再把事实、解释与尚未确认的部分分开。最后写下什么变化会支持原判断，什么变化会让它失效。",
          "这套顺序并不保证结论正确，但能减少事后重写，也让读者知道一段文字可以被相信到哪一步。"
        ],
        note: {
          label: "READING NOTE",
          title: "先看来源，再看判断。",
          body: "涉及数据或外部材料时，页面会尽量保留资料日期、来源类型和缺失项。没有证据的位置会保持空白。"
        }
      },
      {
        id: "what",
        title: "你会在这里看到什么",
        paragraphs: [
          "这里会逐步出现三类内容：关于市场与行业的公开研究手记，关于证据整理和复盘的方法文章，以及围绕 AI、数据和个人工具的实验记录。它们共享同一套文章结构，但不会被包装成同一种结论。",
          "更新不会追求日更。只有当材料足以形成一页完整记录时，它才会成为公开文章。未完成的想法会留在工作台，而不是用占位内容填满首页。"
        ]
      },
      {
        id: "boundary",
        title: "一条始终保留的边界",
        paragraphs: [
          "云宸LAB是个人研究与方法交流空间，不连接券商，不生成订单，也不提供针对个人的买卖、价格或仓位指令。公开内容不能替代独立判断，更不能替代具有相应资格的专业服务。",
          "欢迎从任何一篇文章开始，也欢迎带着问题回来。这个网站真正想积累的，不是答案的数量，而是越来越清楚的研究过程。"
        ]
      }
    ],
    closing: "这是云宸LAB的第一页。以后每一页都会尽量带着来处，也留下可以被修正的余地。",
    sources: [
      { label: "内容范围", detail: "云宸LAB公开站点定位与研究边界" },
      { label: "插画说明", detail: "封面插画由 AI 辅助生成，云宸LAB筛选与定稿" }
    ]
  },
  {
    slug: "clues-facts-and-interpretations",
    label: "LAB NOTE / 02",
    category: "研究方法",
    title: "线索、事实和解释，为什么要分开摆放",
    summary: "同一段市场叙事里，线索提醒我们去看，事实告诉我们已经发生了什么，解释则是暂时建立的关系。把三者混在一起，最容易制造虚假的确定感。",
    publishedAt: "2026-09-04",
    updatedAt: "2026-09-04",
    readingMinutes: 8,
    cover: "/assets/article-evidence-cover-v1.png",
    coverAlt: "不同颜色的资料纸页围绕放大镜连接，并保留虚线空白的编辑插画",
    accent: "yellow",
    sections: [
      {
        id: "clue",
        title: "线索负责提醒，不负责证明",
        paragraphs: [
          "新闻标题、价格异动、讨论热度和一句未经核对的转述，都可能是一条线索。线索的价值是改变注意力的方向：这里也许发生了值得继续看的变化。",
          "问题在于，线索很容易被写成结论。一次同步上涨可能来自共同驱动，也可能只是时间上的巧合；一句政策表述可能改变预期，也可能尚未形成可执行的影响。线索进入记录时，最重要的字段不是态度，而是来源和时间。"
        ]
      },
      {
        id: "fact",
        title: "事实需要口径，也需要边界",
        paragraphs: [
          "事实并不只是一个数字。它至少还包括统计范围、时间区间、计算方式和来源。缺少这些信息，同一个数字可以支持完全不同的故事。",
          "把事实单独摆放，是为了让它能够被复算、被替换，也能够在来源冲突时保持原样。遇到缺失数据，最稳妥的做法不是自动补零，而是明确写下缺失及其影响。"
        ],
        note: {
          label: "EVIDENCE CHECK",
          title: "一条事实的最小记录。",
          body: "对象、时间、口径、来源、缺失与冲突。缺少其中任何一项，都应降低后续解释的强度。"
        }
      },
      {
        id: "interpretation",
        title: "解释是一座可以拆掉的桥",
        paragraphs: [
          "解释把几条事实连接成一种暂时可用的关系。它可能来自产业逻辑、历史经验，也可能只是当前最合理的假设。解释有用，是因为它帮助我们提出下一步问题；解释危险，是因为它很容易被误认为事实本身。",
          "因此，每个解释都应当附带反方证据和失效条件。后来出现的新材料不是对文章的冒犯，而是检验这座桥是否还值得保留。"
        ],
        quote: "好的解释不要求永远正确，它要求在错误出现时能够被发现。"
      },
      {
        id: "practice",
        title: "把三层写在同一页上",
        paragraphs: [
          "实际记录时，可以先写下触发注意的线索，再列出已经核对的事实，最后才写解释。把未知项放在解释旁边，而不是藏在文章末尾。这样读者可以跳过观点，直接检查材料；作者也能在未来只更新发生变化的那一层。",
          "这并不会让研究变慢。相反，它减少了反复寻找原始材料和修补叙事的时间，也让复盘从情绪判断变成一次结构化检查。"
        ]
      }
    ],
    closing: "线索让问题出现，事实给问题落脚，解释让问题继续向前。三者分开，研究才有重新开始的地方。",
    sources: [
      { label: "方法来源", detail: "云宸LAB公开研究记录方法 v1" },
      { label: "插画说明", detail: "封面插画由 AI 辅助生成，云宸LAB筛选与定稿" }
    ]
  }
];

function ArticleBrandHeader(): React.JSX.Element {
  return <header className="article-site-header">
    <a className="article-site-brand" href="#/home" aria-label="返回云宸LAB首页"><b>云宸LAB</b><small>YUNCHEN LAB</small></a>
    <nav aria-label="文章页导航">
      <a href="#/home">首页</a>
      <a href="#/articles">个人文章</a>
      <a href="#/home?section=lab-line-work&tab=research">市场研究</a>
      <a href="#/home?section=lab-line-work&tab=skills">SKILL推荐</a>
      <a href="#/home?section=lab-line-contact">联系</a>
      <button className="article-site-account" type="button" aria-label="登录功能建设中" title="登录功能建设中" disabled><svg viewBox="0 0 48 48" aria-hidden="true"><circle cx="24" cy="24" r="20" /><circle cx="24" cy="18" r="6" /><path d="M13 38c1.8-7 6-10.5 11-10.5S33.2 31 35 38" /></svg></button>
    </nav>
  </header>;
}

function ArticleFooter(): React.JSX.Element {
  return <footer className="article-site-footer"><PublicFooterLegalLinks /></footer>;
}

function useArticleDocument(title: string, summary: string): void {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    const previousDescription = description?.content;
    document.title = `${title}｜云宸LAB`;
    if (description) description.content = summary;
    window.scrollTo({ top: 0 });
    return () => {
      document.title = previousTitle;
      if (description && previousDescription !== undefined) description.content = previousDescription;
    };
  }, [summary, title]);
}

export function ArticleIndexPage(): React.JSX.Element {
  useArticleDocument("文章", "云宸LAB公开文章：市场研究手记、证据整理与复盘方法。");
  return <div className="article-site article-index-page">
    <a className="skip-link" href="#article-index-main">跳到正文</a><ArticleBrandHeader />
    <main id="article-index-main">
      <section className="article-index-hero"><span>ARTICLES / 公开文章</span><h1>把材料放在桌上，<br />把判断写回时间里。</h1><p>这里收录已经完成的公开手记。草稿不会占据目录，所有可见入口都可以真正打开。</p></section>
      <section className="article-index-list" aria-label="文章列表">
        {PUBLIC_ARTICLES.map((article, index) => <a className={`article-index-row article-accent-${article.accent}`} href={`#/articles/${article.slug}`} key={article.slug}>
          <div className="article-index-number">{String(index + 1).padStart(2, "0")}</div>
          <div className="article-index-image"><img src={article.cover} alt="" /></div>
          <div className="article-index-copy"><span>{article.category} · {article.publishedAt}</span><h2>{article.title}</h2><p>{article.summary}</p><small>{article.readingMinutes} 分钟阅读 →</small></div>
        </a>)}
      </section>
    </main><ArticleFooter />
  </div>;
}

export function WorkspaceUnavailablePage(): React.JSX.Element {
  useArticleDocument("研究台正在建设中", "云宸LAB研究台正在建设中，敬请期待。");
  return <div className="article-site article-not-found"><ArticleBrandHeader /><main><span>RESEARCH DESK / BUILDING</span><h1>研究台正在建设中，敬请期待。</h1><p>公开文章可以正常阅读，私人研究工作台暂不对外开放。</p><a href="#/home">返回云宸LAB首页 →</a></main><ArticleFooter /></div>;
}

export function PrivacyPage(): React.JSX.Element {
  useArticleDocument("隐私政策", "云宸LAB隐私政策与联系说明。");
  return <div className="article-site article-privacy-page"><a className="skip-link" href="#article-main">跳到正文</a><ArticleBrandHeader /><main id="article-main">
    <header><span>PRIVACY / 隐私政策</span><h1>尊重你的信息，<br />只保留必要部分。</h1><p>更新日期：2026-09-04</p></header>
    <article>
      <section><h2>网站会处理哪些信息</h2><p>当前公开网站不提供注册、评论或在线表单，也不主动设置广告或行为追踪 Cookie。为保障网站正常运行，托管服务可能记录常规访问与安全日志，例如访问时间、IP 地址、浏览器类型和请求页面。</p></section>
      <section><h2>当你通过邮件联系</h2><p>你主动发送的邮箱地址、邮件内容和附件会由邮箱服务处理，并用于回复你的问题。请不要在邮件中发送与沟通目的无关的敏感个人信息。</p></section>
      <section><h2>政策更新与联系</h2><p>如果网站以后增加登录、统计、评论或其他需要处理个人信息的功能，这一页会同步更新。有关隐私的问题可以发送至 <a href="mailto:yunchenlab@yeah.net">yunchenlab@yeah.net</a>。</p></section>
    </article>
  </main><ArticleFooter /></div>;
}

function articleSectionFromHash(): string | null {
  const queryStart = window.location.hash.indexOf("?");
  if (queryStart < 0) return null;
  return new URLSearchParams(window.location.hash.slice(queryStart + 1)).get("section");
}

function scrollToArticleSection(article: PublicArticle, sectionId: string, requestedBehavior: ScrollBehavior): boolean {
  if (!article.sections.some((section) => section.id === sectionId)) return false;
  const element = document.getElementById(sectionId);
  if (!element) return false;
  const reducedMotion = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  element.scrollIntoView({ behavior: reducedMotion || requestedBehavior === "auto" ? "auto" : "smooth", block: "start" });
  return true;
}

export function ArticlePage({ slug }: { slug: string }): React.JSX.Element {
  const article = PUBLIC_ARTICLES.find((item) => item.slug === slug);
  const [activeSection, setActiveSection] = useState(article?.sections[0]?.id ?? "");
  const sectionNavigationFrame = useRef<number | null>(null);
  useArticleDocument(article?.title ?? "文章未找到", article?.summary ?? "没有找到这篇云宸LAB文章。");
  useEffect(() => {
    if (!article) return;
    let frame = 0;
    const updateActiveSection = (): void => {
      frame = 0;
      const readingLine = Math.min(220, window.innerHeight * .3);
      let current = article.sections[0]?.id ?? "";
      for (const section of article.sections) {
        const element = document.getElementById(section.id);
        if (element && element.getBoundingClientRect().top <= readingLine) current = section.id;
      }
      setActiveSection(current);
    };
    const onScroll = (): void => {
      if (!frame) frame = window.requestAnimationFrame(updateActiveSection);
    };
    const locateSectionFromHash = (behavior: ScrollBehavior): void => {
      const sectionId = articleSectionFromHash();
      if (!sectionId || !article.sections.some((section) => section.id === sectionId)) return;
      if (sectionNavigationFrame.current) window.cancelAnimationFrame(sectionNavigationFrame.current);
      sectionNavigationFrame.current = window.requestAnimationFrame(() => {
        sectionNavigationFrame.current = null;
        scrollToArticleSection(article, sectionId, behavior);
        updateActiveSection();
      });
    };
    const onHashChange = (): void => locateSectionFromHash("auto");
    updateActiveSection();
    locateSectionFromHash("auto");
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    window.addEventListener("hashchange", onHashChange);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      if (sectionNavigationFrame.current) window.cancelAnimationFrame(sectionNavigationFrame.current);
      sectionNavigationFrame.current = null;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("hashchange", onHashChange);
    };
  }, [article]);
  if (!article) return <div className="article-site article-not-found"><ArticleBrandHeader /><main><span>404 / ARTICLE NOT FOUND</span><h1>这页还没有写下来。</h1><p>链接可能已经调整，也可能这篇文章仍在草稿里。</p><a href="#/articles">查看已经发布的文章 →</a></main><ArticleFooter /></div>;

  const articleIndex = PUBLIC_ARTICLES.findIndex((item) => item.slug === slug);
  const previous = PUBLIC_ARTICLES[articleIndex - 1];
  const next = PUBLIC_ARTICLES[articleIndex + 1];
  return <div className={`article-site article-page article-accent-${article.accent}`}>
    <a className="skip-link" href="#article-main">跳到正文</a><ArticleBrandHeader />
    <main id="article-main">
      <header className="article-hero">
        <div className="article-hero-copy"><a href="#/articles">ARTICLES / 返回文章</a><span>{article.label} · {article.category}</span><h1>{article.title}</h1><p>{article.summary}</p><dl><div><dt>发布</dt><dd>{article.publishedAt}</dd></div><div><dt>更新</dt><dd>{article.updatedAt}</dd></div><div><dt>阅读</dt><dd>{article.readingMinutes} 分钟</dd></div></dl></div>
        <figure><img src={article.cover} alt={article.coverAlt} /></figure>
      </header>
      <div className="article-reading-grid">
        <aside className="article-toc"><span>ON THIS PAGE</span><nav aria-label="文章目录">{article.sections.map((section, index) => {
          const sectionHref = `#/articles/${article.slug}?section=${encodeURIComponent(section.id)}`;
          return <a href={sectionHref} key={section.id} aria-current={activeSection === section.id ? "location" : undefined} onClick={(event) => {
            event.preventDefault();
            setActiveSection(section.id);
            scrollToArticleSection(article, section.id, "smooth");
            if (window.location.hash !== sectionHref) window.location.hash = sectionHref;
          }}><small>{String(index + 1).padStart(2, "0")}</small><span className="article-toc-label"><span>{section.title}</span><strong aria-hidden="true">{section.title}</strong></span></a>;
        })}</nav></aside>
        <article className="article-prose">
          {article.sections.map((section) => <section id={section.id} key={section.id}><h2>{section.title}</h2>{section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}{section.quote && <blockquote>{section.quote}</blockquote>}{section.note && <aside className="article-note"><span>{section.note.label}</span><h3>{section.note.title}</h3><p>{section.note.body}</p></aside>}</section>)}
          <div className="article-closing"><span>END NOTE</span><p>{article.closing}</p></div>
          <section className="article-sources" aria-labelledby="article-sources-title"><h2 id="article-sources-title">来源与说明</h2><dl>{article.sources.map((source) => <div key={source.label}><dt>{source.label}</dt><dd>{source.detail}</dd></div>)}</dl></section>
        </article>
      </div>
      <nav className="article-neighbors" aria-label="相邻文章">{previous ? <a href={`#/articles/${previous.slug}`}><span>上一篇</span><b>{previous.title}</b></a> : <span />}{next ? <a href={`#/articles/${next.slug}`}><span>下一篇</span><b>{next.title}</b></a> : <a href="#/articles"><span>继续阅读</span><b>查看全部文章</b></a>}</nav>
    </main><ArticleFooter />
  </div>;
}
